import React from 'react';
import moment from 'moment-timezone';

import APP_CONFIG from 'config/app-config';
import {
  deleteCookies,
  fixNumber,
  formatCurrency,
  formatNumber,
  getCurrencySymbol,
} from './helper';
import { currencyColumnOptions, normalizeCurrencyCode } from './currencies';
import { CommentIcon } from 'utils/icons';
import ALL_ROUTES from 'config/routes';
import { queryClient } from '../App';
import store, { RESET_STORE } from '../store';

export const getTokenInfo = () => {
  try {
    const savedSession = JSON.parse(
      localStorage.getItem(APP_CONFIG.sessionVariables.AUTH_TOKEN) || null
    );
    return savedSession;
  } catch (e) {
    return '';
  }
};
export const isTokenValid = tokenInfo => {
  tokenInfo ||= getTokenInfo();
  if (
    tokenInfo?.expiryTime &&
    moment(tokenInfo?.expiryTime).diff(moment(new Date()), 'minutes') <= 0
  ) {
    return false;
  }

  return true;
};

export const getAuthToken = () => {
  const savedSession = getTokenInfo();
  return isTokenValid(savedSession) && savedSession?.token;
};

export const userTokenExists = () => {
  return Boolean(getAuthToken());
};

export const checkAndSetUserLoggedIn = setIsUserLoggedIn => {
  const userTokenFound = userTokenExists();
  if (userTokenFound) {
    setIsUserLoggedIn(true);
  }
};

export const getUserData = () => {
  try {
    return JSON.parse(
      localStorage.getItem(APP_CONFIG.sessionVariables.USER_INFO || null)
    );
  } catch (e) {
    return '';
  }
};

/**
 *
 * @param {Boolean} rememberMe
 * @param {String} email
 * @param {String} password
 */
export const handleRememberMeSetting = (rememberMe, email) => {
  if (rememberMe) {
    localStorage.setItem('email', email);
    localStorage.setItem('remember_me', true);
  } else {
    localStorage.removeItem('email');
    localStorage.removeItem('remember_me');
  }
};

export const handleLoginSuccess = (token, userData, navigate) => {
  let expiryTime;
  const getDateTime = date => moment.utc(date).tz(moment.tz.guess());
  if (localStorage.getItem('remember_me')) {
    expiryTime = getDateTime(new Date()).add(90, 'd');
  } else {
    expiryTime = getDateTime(new Date()).add(16, 'h');
  }

  localStorage.setItem(
    APP_CONFIG.sessionVariables.AUTH_TOKEN,
    JSON.stringify({ token, expiryTime })
  );
  localStorage.setItem(
    APP_CONFIG.sessionVariables.USER_INFO,
    JSON.stringify({
      ...userData,
    })
  );

  navigate(ALL_ROUTES.HOME_SCREEN);
};

export const replaceAuthToken = newToken => {
  const existing = getTokenInfo();
  const expiryTime = existing?.expiryTime;
  localStorage.setItem(
    APP_CONFIG.sessionVariables.AUTH_TOKEN,
    JSON.stringify({ token: newToken, expiryTime })
  );
};

export const carryOutLogout = setIsUserLoggedIn => {
  localStorage.removeItem(APP_CONFIG.sessionVariables.USER_INFO);
  localStorage.removeItem(APP_CONFIG.sessionVariables.AUTH_TOKEN);
  localStorage.removeItem(APP_CONFIG.sessionVariables.SELECTED_TAB);
  localStorage.removeItem(APP_CONFIG.sessionVariables.OPEN_TABS);
  sessionStorage.clear();
  deleteCookies();

  // Cancel in-flight queries and clear all cached data
  queryClient.cancelQueries();
  queryClient.clear();

  // Reset all Redux state to initial values
  store.dispatch({ type: RESET_STORE });

  setIsUserLoggedIn(false);
};

export const getSelectedTabFromCache = () => {
  try {
    return JSON.parse(
      localStorage.getItem(APP_CONFIG.sessionVariables.SELECTED_TAB) || null
    );
  } catch (e) {
    return '';
  }
};

export const setSelectedTabInCache = selectedTab => {
  try {
    return localStorage.setItem(
      APP_CONFIG.sessionVariables.SELECTED_TAB,
      JSON.stringify(selectedTab)
    );
  } catch (e) {
    return '';
  }
};
export const prepareDropDownOptions = (options, labelKey, valueKey) => {
  return (
    options?.map(item => ({
      label: item[labelKey],
      value: item[valueKey]?.toString(),
    })) || []
  );
};

export const getDetailsSheet = (item, type) => {
  return type === 'swimlane'
    ? item?.swimlane_detail_sheets
    : item?.swimlane_item_detail_sheets;
};

export const deleteSwimlaneDetailsItem = (
  qClient,
  selectedItem,
  selectedSwimlane,
  swimlanePayload
) => {
  const swimlaneData = qClient.getQueryData([
    'get-swimlanes-in-budget',
    swimlanePayload,
  ]);
  const swimlaneItems = swimlaneData?.data || [];
  const selectedSwimlaneItem = {
    ...(swimlaneItems?.find(
      swimlaneItem => swimlaneItem?.id === selectedSwimlane?.id
    ) || {}),
  };
  const swimlaneDetails =
    getDetailsSheet(selectedSwimlaneItem, swimlanePayload.type) || [];
  selectedSwimlaneItem[
    swimlanePayload.type === 'swimlane'
      ? 'swimlane_detail_sheets'
      : 'swimlane_item_detail_sheets'
  ] = swimlaneDetails.filter(row => row?.id !== selectedItem?.id) || [];

  qClient.setQueryData(['get-swimlanes-in-budget', swimlanePayload], {
    ...swimlaneData,
    data:
      swimlaneItems?.map(swimlaneItem =>
        swimlaneItem?.id === selectedSwimlane?.id
          ? selectedSwimlaneItem
          : swimlaneItem
      ) || [],
  });
};

export const compareFormValues = (newValues, defaultValues) => {
  return Object.keys(newValues)?.some(field => {
    const changedValue =
      typeof newValues?.[field] === 'object'
        ? newValues?.[field]?.value || ''
        : newValues?.[field];

    return changedValue !== defaultValues?.[field];
  });
};

export const prepareProfileInfo = (userProfile, userData) => ({
  user_type: (userProfile?.user_type || userData?.user_type || '')?.toString(),
  name: (userProfile?.name || userData?.name)?.trim(),
  job_title: (userProfile?.job_title ?? userData?.job_title ?? '')?.trim(),
});

export const prepareContactInfo = (userProfile, userData) => ({
  phone_number: userProfile?.userprofile?.phone_number || '',
  default_currency: normalizeCurrencyCode(
    userProfile?.userprofile?.default_currency
  ),
  email: userData?.email,
});

export const prepareAddressInfo = userProfile => ({
  address_line_1: userProfile?.useraddress?.address_line_1 || '',
  address_line_2: userProfile?.useraddress?.address_line_2 || '',
  country: userProfile?.useraddress?.country || '',
  state: userProfile?.useraddress?.state || '',
  city: userProfile?.useraddress?.city || '',
  zip_code: userProfile?.useraddress?.zip_code || '',
});

// export const calculateDetailsTotal = (newDetails, setDetailsTotal) => {
//   const uniqueValues = newDetails?.reduce((acc, item) => {
//     return {
//       ...acc,
//       [item?.id]: isNaN(item?.item_total)
//         ? 0
//         : parseFloat(item?.item_total) || 0,
//     };
//   }, {});
//   const sum = Object.values(uniqueValues).reduce((total, item) => {
//     total += item;
//     return total;
//   }, 0);
//   typeof setDetailsTotal === 'function' &&
//     setDetailsTotal(parseFloat(sum?.toFixed(2)));
//   return parseFloat(sum?.toFixed(2));

export const calculateDetailsTotal = (newDetails, setDetailsTotal) => {
  const sum = newDetails?.reduce((total, item) => {
    const itemTotal = isNaN(item?.item_total)
      ? 0
      : parseFloat(item?.item_total) || 0;
    const fringeTotal = isNaN(item?.fringe_total)
      ? 0
      : parseFloat(item?.fringe_total) || 0;
    return total + itemTotal + fringeTotal;
  }, 0);
  const total = parseFloat(sum?.toFixed(2)) || 0;
  typeof setDetailsTotal === 'function' && setDetailsTotal(total);
  return total;
};

// const itemsTotal = newDetails?.reduce((total, item) => {
//   total += isNaN(item?.item_total) ? 0 : parseFloat(item?.item_total) || 0;
//   return total;
// }, 0);

// typeof setDetailsTotal === 'function' &&
//   setDetailsTotal(parseFloat(itemsTotal?.toFixed(2)));
// console.error('[calculateDetailsTotal] itemsTotal', itemsTotal);
// return parseFloat(itemsTotal?.toFixed(2));
// };

// export const handlePartialDataUpdate = (
//   existingRows,
//   updatedRows,
//   selectedSwimlane,
//   type
// ) => {
//   console.log('existingRows', existingRows);
//   console.log('updatedRows', updatedRows);
//   console.log('selectedSwimlane', selectedSwimlane);
//   console.log('type', type);
//   // const uniqueRows =
//   //   updatedRows?.reduce((acc, row) => {
//   //     return { ...acc, [row?.row_id ? row?.row_id : row?.id]: row };
//   //   }, {}) || {};

//   const uniqueRows = {};
//   updatedRows?.forEach(row => {
//     console.log('row', row);
//     uniqueRows[row?.row_id ? row?.row_id : row?.id] = row;
//   });

//   console.log('uniqueRows', uniqueRows);

//   let index = 0;
//   if (existingRows?.length) {
//     index = Math.max(...existingRows.map(row => row.row_order));
//   } else {
//     existingRows = [
//       {
//         // id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
//         row_order: (updatedRows?.length || 0) + 1,
//         row_id: `row-${type}-${selectedSwimlane?.id}-${
//           updatedRows?.length + 1
//         }`,
//       },
//     ];
//   }
//   index++;

//   let allRows = existingRows.map(item => {
//     const row = uniqueRows[item?.row_id] || uniqueRows[item?.id];
//     if (row) {
//       delete uniqueRows[item?.row_id];
//       delete uniqueRows[item?.id];
//     }

//     return row
//       ? {
//           ...row,
//           row_id: item?.row_id,
//           row_order: item?.row_order,
//         }
//       : item;
//   });

//   console.log('allRows', allRows);

//   allRows = [
//     ...Object.values(uniqueRows).map(row => ({
//       ...row,
//       row_id: `row-${type}-${selectedSwimlane?.id}-${index}`,
//       row_order: index++,
//     })),
//     ...allRows,
//   ];

//   console.log('allRows#', allRows);

//   return allRows;
// };

/**
 * Merge backend rows with existing local rows.
 * When dirtyRows is provided, rows in that set keep their local version to avoid refetch overwriting unsaved edits.
 * @param {Array} existingRows - Current rows in UI
 * @param {Array} updatedRows - Rows from backend (e.g. refetch)
 * @param {object} selectedSwimlane - Current swimlane/item context
 * @param {string} type - 'swimlane' | 'line-item' | 'line-item-children'
 * @param {Set} [dirtyRows] - Optional set of row ids (id or row_id) that have pending local changes; these keep local version
 */
export const handlePartialDataUpdate = (
  existingRows,
  updatedRows,
  selectedSwimlane,
  type,
  dirtyRows = new Set()
) => {
  // No server data — keep existing rows as-is
  if (!updatedRows?.length) {
    return existingRows?.length ? existingRows : [];
  }

  const serverRows = updatedRows || [];
  const serverById = new Map(
    serverRows.filter(row => row?.id != null).map(row => [String(row.id), row])
  );
  const consumedServerIds = new Set();

  const mergeServerRow = (serverRow, localRow, i) => {
    const rowKey = localRow?.row_id ?? localRow?.id ?? serverRow?.id;
    const isDirty =
      (dirtyRows?.has?.(rowKey) ?? false) ||
      (serverRow?.id != null && (dirtyRows?.has?.(serverRow.id) ?? false));

    if (isDirty && localRow) {
      return {
        ...localRow,
        id: serverRow?.id ?? localRow.id,
        created_on: serverRow?.created_on ?? localRow.created_on,
        updated_on: serverRow?.updated_on ?? localRow.updated_on,
        legacy_swimlane_detail_id:
          serverRow?.legacy_swimlane_detail_id ??
          localRow.legacy_swimlane_detail_id,
        legacy_line_item_detail_id:
          serverRow?.legacy_line_item_detail_id ??
          localRow.legacy_line_item_detail_id,
        is_placeholder: false,
        _isDraft: false,
      }; // Keep local values for dirty rows while adopting server identity.
    }

    return {
      ...serverRow,
      row_id:
        localRow?.row_id ??
        `row-${type}-${selectedSwimlane?.id}-${serverRow?.id ?? i + 1}`,
      row_order: localRow?.row_order ?? serverRow?.position ?? i + 1,
      position: localRow?.position ?? serverRow?.position ?? i,
      is_placeholder:
        localRow?.is_placeholder ?? serverRow?.is_placeholder ?? false,
    };
  };

  const decimalDetailFields = new Set([
    'quantity',
    'rate',
    'multiplier',
    'item_total',
    'fringe_total',
    'column_total',
  ]);

  const normalizeComparableDetailValue = (field, value) => {
    if (value === null || value === undefined || value === '') return '';
    if (!decimalDetailFields.has(field)) return String(value).trim();

    const fixed = fixNumber(value);
    const numeric = Number.parseFloat(fixed);
    return Number.isFinite(numeric) ? numeric.toFixed(4) : String(value).trim();
  };

  const isSameDraftPosition = (draftRow, serverRow) => {
    const draftPosition = draftRow?.position ?? draftRow?.row_order;
    const serverPosition = serverRow?.position ?? serverRow?.row_order;

    return (
      draftPosition !== null &&
      draftPosition !== undefined &&
      serverPosition !== null &&
      serverPosition !== undefined &&
      Number(draftPosition) === Number(serverPosition)
    );
  };

  const hasNearbyDraftPosition = (draftRow, serverRow) => {
    const draftPosition = draftRow?.position ?? draftRow?.row_order;
    const serverPosition = serverRow?.position ?? serverRow?.row_order;

    if (
      draftPosition === null ||
      draftPosition === undefined ||
      serverPosition === null ||
      serverPosition === undefined
    ) {
      return true;
    }

    return Math.abs(Number(draftPosition) - Number(serverPosition)) <= 1;
  };

  const hasExplicitNearbyDraftPosition = (draftRow, serverRow) => {
    const draftPosition = draftRow?.position ?? draftRow?.row_order;
    const serverPosition = serverRow?.position ?? serverRow?.row_order;

    if (
      draftPosition === null ||
      draftPosition === undefined ||
      serverPosition === null ||
      serverPosition === undefined
    ) {
      return false;
    }

    return Math.abs(Number(draftPosition) - Number(serverPosition)) <= 1;
  };

  const isDraftRepresentedByServerRow = (draftRow, serverRow) => {
    if (!draftRow || !serverRow || draftRow?.is_placeholder) {
      return false;
    }

    const draftKey = draftRow?.row_id ?? draftRow?.id;
    const isDirtyDraft =
      (draftKey != null && (dirtyRows?.has?.(draftKey) ?? false)) ||
      (draftKey != null && (dirtyRows?.has?.(String(draftKey)) ?? false));
    if (isDirtyDraft && hasExplicitNearbyDraftPosition(draftRow, serverRow)) {
      return true;
    }

    const comparableFields = [
      'item',
      'account_number',
      'currency',
      'quantity',
      'unit',
      'rate',
      'multiplier',
      'multiplier_unit',
      'comment',
    ];

    const contentMatches = comparableFields.every(
      field =>
        normalizeComparableDetailValue(field, draftRow[field]) ===
        normalizeComparableDetailValue(field, serverRow[field])
    );

    return (
      contentMatches &&
      (isSameDraftPosition(draftRow, serverRow) ||
        hasNearbyDraftPosition(draftRow, serverRow))
    );
  };

  const consumeDraftServerMatch = draftRow => {
    const serverRow = serverRows.find(row => {
      if (row?.id == null || consumedServerIds.has(String(row.id))) {
        return false;
      }
      return isDraftRepresentedByServerRow(draftRow, row);
    });

    if (serverRow?.id != null) {
      consumedServerIds.add(String(serverRow.id));
    }

    return serverRow;
  };

  const mergedRows = [];
  (existingRows || []).forEach((localRow, i) => {
    if (!localRow) return;

    if (localRow.id != null) {
      const serverRow = serverById.get(String(localRow.id));
      if (serverRow) {
        consumedServerIds.add(String(serverRow.id));
        mergedRows.push(mergeServerRow(serverRow, localRow, i));
      }
      return;
    }

    if (localRow.row_id) {
      const serverRow = consumeDraftServerMatch(localRow);
      if (serverRow) {
        mergedRows.push(mergeServerRow(serverRow, localRow, i));
      } else {
        mergedRows.push(localRow);
      }
    }
  });

  serverRows.forEach(serverRow => {
    if (serverRow?.id != null && consumedServerIds.has(String(serverRow.id))) {
      return;
    }
    mergedRows.push(mergeServerRow(serverRow, null, mergedRows.length));
  });

  const placeholderRows = mergedRows.filter(row => row?.is_placeholder);
  const nonPlaceholderRows = mergedRows.filter(row => !row?.is_placeholder);
  return [...nonPlaceholderRows, ...placeholderRows];
};

export const formatTotal = currency => value => {
  value = fixNumber(value);

  const formatted =
    value !== undefined && value !== null && value !== ''
      ? formatCurrency(value, currency)
      : getCurrencySymbol(currency);
  return formatted;
};

export const formatMultiplier = value => {
  value = fixNumber(value, false);
  return value !== undefined &&
    value !== null &&
    value >= 0 &&
    !Number.isNaN(value)
    ? parseFloat(value)
    : value;
};

export const formatRate = currency => value => {
  if (value === null || value === undefined || value === '') return '';
  value = fixNumber(value);
  if (value === null || value === undefined || value === '') return '';
  const formatted =
    (value && !Number.isNaN(value)
      ? formatNumber(value, currency)
      : parseFloat(value || 0)) || '';
  return !/^0(\.00)?$/.test(value) && /^\D?0\.00$/.test(formatted)
    ? ''
    : formatted;
};

export const formatOptionalCurrencyAmount = currency => value => {
  if (value === null || value === undefined || value === '') return '';
  value = fixNumber(value);
  if (value === null || value === undefined || value === '') return '';
  return Number.isNaN(value) ? '' : formatNumber(value, currency);
};

const stopColumnHeaderControlEvent = event => {
  event.stopPropagation();
};

const stopColumnHeaderControlDrag = event => {
  event.preventDefault();
  event.stopPropagation();
};

export const OVERTIME_COLUMNS = [
  { slot: 1, defaultLabel: '1.5 X', defaultMultiplier: 1.5 },
  { slot: 2, defaultLabel: 'Double OT', defaultMultiplier: 2 },
  { slot: 3, defaultLabel: 'Triple OT', defaultMultiplier: 3 },
  { slot: 4, defaultLabel: '4X OT', defaultMultiplier: 4 },
];

export const OVERTIME_COLUMN_KEYS = OVERTIME_COLUMNS.map(
  ({ slot }) => `ot${slot}_quantity`
);

export const OVERTIME_RATE_COLUMN_KEYS = OVERTIME_COLUMNS.map(
  ({ slot }) => `ot${slot}_rate`
);

const getOvertimeColumns = selectedBudget =>
  OVERTIME_COLUMNS.flatMap(({ slot, defaultLabel, defaultMultiplier }) => {
    const overtimeLabel = selectedBudget?.[`ot${slot}_label`] ?? defaultLabel;
    const overtimeMultiplier =
      selectedBudget?.[`ot${slot}_multiplier`] ?? defaultMultiplier;
    const overtimeStart = selectedBudget?.[`ot${slot}_start`] ?? null;
    const overtimeEnd = selectedBudget?.[`ot${slot}_end`] ?? null;
    const quantityKey = `ot${slot}_quantity`;

    return [
      {
        label: `OT${slot} ${overtimeLabel}`.trim(),
        managerLabel: `OT${slot}`,
        overtimeLabel,
        overtimeMultiplier: String(overtimeMultiplier),
        overtimeStart,
        overtimeEnd,
        overtimeSlot: slot,
        isOvertime: true,
        columnGroupKey: quantityKey,
        visibilityKey: quantityKey,
        key: quantityKey,
        dataType: 'decimal',
        width: '132px',
        type: 'text',
        placeholder: '0',
        required: false,
        formatValue: formatMultiplier,
        defaultValue: '0',
        defaultMultiplier: String(defaultMultiplier),
        allowNegative: false,
      },
      {
        label: `OT${slot} Rate`,
        managerLabel: `OT${slot}`,
        overtimeSlot: slot,
        isOvertimeRate: true,
        columnGroupKey: quantityKey,
        visibilityKey: quantityKey,
        key: `ot${slot}_rate`,
        dataType: 'decimal',
        width: '112px',
        type: 'showValue',
        prefix: getCurrencySymbol(selectedBudget?.primary_currency),
        formatValue: formatRate(selectedBudget?.primary_currency),
        required: false,
      },
    ];
  });

export const getColumnConf = (
  selectedBudget,
  unitTypes,
  _toggleColumn,
  setFocus,
  focus,
  hasFringes,
  showAccountNumbers = false,
  compactColumn = {},
  _toggleCompactColumn,
  showOvertimeColumns = true
) => {
  const toggleCommentColumn = _toggleColumn?.('comment');
  const toggleCommentColumnSize =
    _toggleCompactColumn?.('comment') || toggleCommentColumn;
  const isCommentCompact = Boolean(compactColumn?.comment);
  return [
    ...(showAccountNumbers
      ? [
          {
            label: 'Account #',
            key: 'account_number',
            width: '110px',
            type: 'text',
            placeholder: 'Account #',
            maxLength: 14,
            required: false,
          },
        ]
      : []),
    {
      label: 'Item',
      key: 'item',
      width: '216px',
      type: 'text',
      placeholder: 'Enter Item...',
      required: true,
    },
    {
      label: 'Tags',
      key: 'tags',
      width: '176px',
      type: 'tags',
      placeholder: 'Tags',
      required: false,
    },
    {
      label: 'Quantity',
      key: 'quantity',
      dataType: 'decimal',
      width: '96px',
      type: 'text',
      placeholder: '1',
      required: false,
      formatValue: formatMultiplier,
      pluralize: {
        key: 'unit',
        valueKey: 'quantity',
      },
      defaultValue: '1',
      allowNegative: false,
    },
    {
      label: 'Unit',
      key: 'unit',
      width: '96px',
      type: 'select',
      placeholder: 'Unit',
      options: unitTypes,
      required: false,
      pluralize: {
        key: 'unit',
        valueKey: 'quantity',
      },
    },
    {
      label: 'Rate',
      key: 'rate',
      dataType: 'decimal',
      width: '96px',
      type: 'text',
      placeholder: '',
      prefix: getCurrencySymbol(selectedBudget?.primary_currency),
      required: true,
      // formatValue: formatMultiplier,
      formatValue: formatRate(selectedBudget?.primary_currency),
      defaultValue: '',
    },
    ...(showOvertimeColumns ? getOvertimeColumns(selectedBudget) : []),
    {
      label: 'Currency',
      key: 'currency',
      width: '118px',
      type: 'select',
      placeholder: normalizeCurrencyCode(selectedBudget?.primary_currency),
      options: currencyColumnOptions,
      required: false,
      defaultValue: normalizeCurrencyCode(selectedBudget?.primary_currency),
    },
    {
      label: 'Multiplier',
      key: 'multiplier',
      dataType: 'decimal',
      width: '96px',
      type: 'text',
      placeholder: '1',
      required: false,
      formatValue: formatMultiplier,
      pluralize: {
        key: 'multiplier_unit',
        valueKey: 'multiplier',
      },
      defaultValue: '1',
      allowNegative: false,
    },
    {
      label: 'Multiplier Unit',
      key: 'multiplier_unit',
      width: '96px',
      type: 'select',
      placeholder: 'Unit',
      options: unitTypes,
      required: false,
      pluralize: {
        key: 'multiplier_unit',
        valueKey: 'multiplier',
      },
    },
    {
      label: 'Item Total',
      key: 'item_total',
      type: 'showValue',
      dataType: 'decimal',
      width: '96px',
      formatValue: formatTotal(selectedBudget?.primary_currency),
      defaultValue: '0',
      required: false,
    },
    {
      label: (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {/* Put F% first */}
          <button
            style={{
              backgroundColor: 'white',
              color: '#4285F4',
              border: 'none',
              padding: '5px 5px',
              borderRadius: '30px',
              cursor: 'pointer',
              fontWeight: 'bold',
            }}
            onClick={() => {
              setFocus(!focus);
            }}
          >
            F%
          </button>
          {hasFringes && <span>Fringes</span>}
        </div>
      ),
      key: 'fringes',
      placeholder: 'Dropdown',
      options: unitTypes,
      required: false,
      width: hasFringes ? '130px' : '50px',
      pluralize: {
        key: 'multiplier_unit',
        valueKey: 'multiplier',
      },
    },
    {
      label: 'Total',
      key: 'column_total',
      type: 'showValue',
      dataType: 'decimal',
      width: '96px',
      formatValue: formatTotal(selectedBudget?.primary_currency),
      required: false,
    },
    {
      label: 'Actual',
      key: 'actual',
      dataType: 'decimal',
      width: '96px',
      type: 'text',
      placeholder: '',
      prefix: getCurrencySymbol(selectedBudget?.primary_currency),
      required: false,
      formatValue: formatOptionalCurrencyAmount(
        selectedBudget?.primary_currency
      ),
      defaultValue: '',
    },
    ...(selectedBudget?.cost_reporting_enabled
      ? [
          {
            label: 'ETC',
            managerLabel: 'Estimate to Complete',
            key: 'estimate_to_complete_override',
            displayValueKey: 'effective_estimate_to_complete',
            dataType: 'decimal',
            width: '112px',
            type: 'text',
            placeholder: 'Auto',
            prefix: getCurrencySymbol(selectedBudget?.primary_currency),
            required: false,
            formatValue: formatOptionalCurrencyAmount(
              selectedBudget?.primary_currency
            ),
            defaultValue: '',
            allowNegative: false,
          },
        ]
      : []),
    {
      label: 'Notes',
      key: 'comment',
      width: isCommentCompact ? '72px' : '190px',
      type: 'text',
      placeholder: 'Notes....',
      isCompact: isCommentCompact,
      icon: (
        <button
          type="button"
          className="detail-column-toggle"
          data-column-header-control="true"
          draggable={false}
          aria-label={
            isCommentCompact ? 'Expand notes column' : 'Shrink notes column'
          }
          aria-pressed={isCommentCompact}
          title={
            isCommentCompact ? 'Expand notes column' : 'Shrink notes column'
          }
          onDragStart={stopColumnHeaderControlDrag}
          onMouseDown={stopColumnHeaderControlEvent}
          onPointerDown={stopColumnHeaderControlEvent}
          onClick={event => {
            event.preventDefault();
            event.stopPropagation();
            toggleCommentColumnSize?.();
          }}
        >
          <CommentIcon />
        </button>
      ),
      onClick: toggleCommentColumnSize,
      required: false,
    },
  ];
};

export const calculateOvertimeUnitRate = (
  itemRow,
  overtimeConfig = itemRow,
  slot
) => {
  const overtimeColumn = OVERTIME_COLUMNS.find(column => column.slot === slot);
  if (!overtimeColumn) return null;

  const parsedRate = Number.parseFloat(fixNumber(itemRow?.rate));
  if (!Number.isFinite(parsedRate)) return null;

  const parsedMultiplier = Number.parseFloat(
    fixNumber(overtimeConfig?.[`ot${slot}_multiplier`], false)
  );
  const overtimeMultiplier =
    Number.isFinite(parsedMultiplier) && parsedMultiplier >= 0
      ? parsedMultiplier
      : overtimeColumn.defaultMultiplier;

  return parsedRate * overtimeMultiplier;
};

export const calculateItemTotal = (itemRow, overtimeConfig = itemRow) => {
  const quantity = fixNumber(itemRow.quantity, false);
  const rate = fixNumber(itemRow.rate);
  const multiplier = fixNumber(itemRow.multiplier, false);
  const parsedQuantity = Number.parseFloat(quantity);
  const parsedRate = Number.parseFloat(rate);
  const parsedMultiplier = Number.parseFloat(multiplier);
  const safeQuantity =
    Number.isFinite(parsedQuantity) && parsedQuantity >= 0 ? parsedQuantity : 1;
  const safeRate = Number.isFinite(parsedRate) ? parsedRate : 0;
  const safeMultiplier =
    Number.isFinite(parsedMultiplier) && parsedMultiplier >= 0
      ? parsedMultiplier
      : 1;
  const overtimeSubtotal = OVERTIME_COLUMNS.reduce(
    (total, { slot, defaultMultiplier }) => {
      const parsedOvertimeQuantity = Number.parseFloat(
        fixNumber(itemRow?.[`ot${slot}_quantity`], false)
      );
      const overtimeQuantity =
        Number.isFinite(parsedOvertimeQuantity) && parsedOvertimeQuantity >= 0
          ? parsedOvertimeQuantity
          : 0;
      const overtimeUnitRate =
        calculateOvertimeUnitRate(itemRow, overtimeConfig, slot) ??
        safeRate * defaultMultiplier;

      return total + overtimeQuantity * overtimeUnitRate;
    },
    0
  );

  return (
    Object.hasOwn(itemRow, 'rate') &&
    (safeQuantity * safeRate + overtimeSubtotal) * safeMultiplier
  );
};
